import { FC } from 'react'
import { Controller } from 'react-hook-form'
import AutocompleteInput from 'components/organisms/AutocompleteInput/AutocompleteInput'
import TextInput from 'components/molecules/TextInput/TextInput'
import { DateInput } from 'components/mui'
import EditIcon from '@mui/icons-material/Edit'
import { Surgery } from 'common/types'
import { useEditProcedureModalLogic } from './EditProcedureModal.logic'
import { CustomModal } from 'components/molecules/Modal/Modal'
import './EditProcedureModal.scss'

interface EditProcedureModalProps {
  open: boolean
  onClose: () => void
  surgery: Surgery
  beforeDate?: Date
  afterDate?: Date
}

const EditProcedureModal: FC<EditProcedureModalProps> = ({
  open,
  onClose,
  surgery,
  beforeDate,
  afterDate,
}) => {
  const {
    control,
    errors,
    surgeons,
    procedures,
    isLoadingSurgeons,
    isLoadingProcedures,
    isLoadingUpdate,
    handleOnSubmit,
  } = useEditProcedureModalLogic(surgery, onClose, beforeDate, afterDate)

  return (
    <CustomModal
      isLoadingState={isLoadingUpdate}
      header="Edit Surgery"
      primaryButtonText="Save Changes"
      open={open}
      secondaryButtonAction={onClose}
      handleOnSubmit={handleOnSubmit}
      primaryButtonDisabled={isLoadingUpdate}
      icon={<EditIcon />}
    >
      <form onSubmit={handleOnSubmit} className="edit-procedure-modal-form">
        <Controller
          name="patientFirstName"
          control={control}
          rules={{
            required: 'Patient first name is required',
          }}
          render={({ field: { onChange, value, ...field } }) => (
            <TextInput
              {...field}
              disabled={isLoadingUpdate}
              required={false}
              value={value}
              handleChangeFormData={onChange}
              variant="outlined"
              placeholder="Patient First Name"
              label="Patient First Name*"
              error={!!errors.patientFirstName}
              helperText={errors.patientFirstName?.message}
              shrink
              sx={{
                marginTop: '20px',
                marginBottom: '20px',
              }}
            />
          )}
        />
        <Controller
          name="patientLastName"
          control={control}
          rules={{
            required: 'Patient last name is required',
          }}
          render={({ field: { onChange, value, ...field } }) => (
            <TextInput
              {...field}
              disabled={isLoadingUpdate}
              required={false}
              value={value}
              handleChangeFormData={onChange}
              variant="outlined"
              placeholder="Patient Last Name"
              label="Patient Last Name*"
              error={!!errors.patientLastName}
              helperText={errors.patientLastName?.message}
              shrink
            />
          )}
        />

        <Controller
          name="surgeon"
          control={control}
          rules={{
            required: 'Surgeon name is required',
          }}
          render={({ field: { onChange, value, ...field } }) => (
            <AutocompleteInput
              {...field}
              disabled={isLoadingUpdate}
              required={false}
              variant="outlined"
              handleChangeFormData={onChange}
              placeholder="Doctor Name"
              label="Doctor Name*"
              options={surgeons}
              error={!!errors.surgeon}
              isLoading={isLoadingSurgeons}
              helperText={errors.surgeon?.message}
              shrink={true}
              value={value}
              sx={{ marginBottom: '0px' }}
            />
          )}
        />

        <Controller
          name="procedure"
          control={control}
          rules={{
            required: 'Procedure name is required',
          }}
          render={({ field: { onChange, value, ...field } }) => (
            <AutocompleteInput
              {...field}
              disabled={isLoadingUpdate}
              required={false}
              variant="outlined"
              handleChangeFormData={onChange}
              placeholder="Procedure Name"
              label="Procedure Name*"
              options={procedures}
              error={!!errors.procedure}
              isLoading={isLoadingProcedures}
              helperText={errors.procedure?.message}
              shrink={true}
              value={value}
              freeSolo
              sx={{
                marginBottom: '20px',
              }}
            />
          )}
        />

        <Controller
          name="patientDob"
          control={control}
          rules={{
            required: 'Date of birth is required',
          }}
          render={({ field: { onChange, value, ...field } }) => (
            <DateInput
              {...field}
              disabled={isLoadingUpdate}
              openTo="month"
              value={value}
              handleChangeFormData={onChange}
              format="YYYY-MM-DD"
              label="Date of Birth*"
              variant="outlined"
              error={!!errors.patientDob}
              helperText={errors.patientDob?.message}
              fullWidth
              sx={{
                my: 0,
                backgroundColor: 'white',
                marginBottom: '20px',
              }}
            />
          )}
        />

        <Controller
          name="mrNumber"
          control={control}
          rules={{
            required: 'MRN is required',
          }}
          render={({ field: { onChange, value, ...field } }) => (
            <TextInput
              {...field}
              disabled={isLoadingUpdate}
              value={value}
              handleChangeFormData={onChange}
              variant="outlined"
              placeholder="MRN#"
              label="MRN#*"
              error={!!errors.mrNumber}
              helperText={errors.mrNumber?.message}
              shrink
              sx={{
                marginBottom: '20px',
              }}
            />
          )}
        />
      </form>
    </CustomModal>
  )
}

export default EditProcedureModal
